import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Filter,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChainStore } from "../store/chainStore";
import { useLayoutStore } from "../store/layoutStore";
import { useCurrentSystem } from "../hooks/useCurrentSystem";
import {
  useStaticSystemsById,
  useWhDestinationByCode,
} from "../hooks/useStaticSystems";
import { useSignatureSaver } from "../graph/useSignatureSaver";
import { isEditableTarget } from "../lib/isEditableTarget";
import { parseScannerClipboard } from "../lib/scannerClipboard";
import { CLASS_LABEL } from "../lib/systemClass";
import type { Signature, SignatureGroup, WhClass } from "../types/chain";
import { SignatureContextMenu } from "./SignatureContextMenu";
import { SignatureGroupIcon } from "./SignatureGroupIcon";
import { SignaturesImportDialog } from "./SignaturesImportDialog";
import { ClassBadge } from "./SystemIdentity";

type SortField = "scan_id" | "name" | "group";
type SortDir = "asc" | "desc";

interface SortState {
  field: SortField;
  dir: SortDir;
}

const SORT_STORAGE_KEY = "signaturesPanel.sort";
const DEFAULT_SORT: SortState = { field: "scan_id", dir: "asc" };

const ALL_GROUPS: readonly SignatureGroup[] = [
  "wormhole",
  "relic",
  "data",
  "gas",
  "combat",
  "ore",
  "unknown",
];

const GROUP_LABELS: Record<SignatureGroup, string> = {
  wormhole: "Wormhole",
  relic: "Relic",
  data: "Data",
  gas: "Gas",
  combat: "Combat",
  ore: "Ore",
  unknown: "Unknown",
};

const FILTER_STORAGE_KEY = "signaturesPanel.filter";

function isSortField(value: unknown): value is SortField {
  return value === "scan_id" || value === "name" || value === "group";
}

function isSortDir(value: unknown): value is SortDir {
  return value === "asc" || value === "desc";
}

function isSignatureGroup(value: unknown): value is SignatureGroup {
  return (
    typeof value === "string" &&
    (ALL_GROUPS as readonly string[]).includes(value)
  );
}

function loadSort(): SortState {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (raw == null) return DEFAULT_SORT;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === "object" &&
      isSortField((parsed as { field?: unknown }).field) &&
      isSortDir((parsed as { dir?: unknown }).dir)
    ) {
      return parsed as SortState;
    }
  } catch {
    /* fall through */
  }
  return DEFAULT_SORT;
}

function saveSort(state: SortState): void {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function loadFilter(): Set<SignatureGroup> {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (raw == null) return new Set(ALL_GROUPS);
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      const groups = parsed.filter(isSignatureGroup);
      return new Set(groups);
    }
  } catch {
    /* fall through */
  }
  return new Set(ALL_GROUPS);
}

function saveFilter(state: Set<SignatureGroup>): void {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify([...state]));
  } catch {
    /* ignore */
  }
}

function SignatureGroupCell({
  sig,
  destinationFor,
}: {
  sig: Signature;
  destinationFor: (code: string) => WhClass | null;
}) {
  const label = (
    <span className="signatures-panel__group-name">
      {GROUP_LABELS[sig.group]}
    </span>
  );
  if (sig.group !== "wormhole" || !sig.wh_type) {
    return label;
  }
  const cls: WhClass | null =
    sig.wh_type === "K162" ? null : destinationFor(sig.wh_type);
  return (
    <>
      {label}
      {cls ? (
        <span
          className={`signatures-panel__wh-class system-class--${cls}`}
        >
          {CLASS_LABEL[cls]}
        </span>
      ) : (
        <span className="signatures-panel__wh-class signatures-panel__wh-class--unknown">
          ?
        </span>
      )}
      <span className="signatures-panel__wh-type">{sig.wh_type}</span>
    </>
  );
}

function compareSignatures(
  a: Signature,
  b: Signature,
  field: SortField,
): number {
  const primary = a[field].localeCompare(b[field], undefined, {
    numeric: true,
    sensitivity: "base",
  });
  if (primary !== 0) return primary;
  return a.scan_id.localeCompare(b.scan_id, undefined, { numeric: true });
}

export function SignaturesPanel() {
  const chainId = useChainStore((s) => s.chainId);
  const systems = useChainStore((s) => s.systems);
  const highlightedSystemId = useLayoutStore((s) => s.highlightedSystemId);
  const currentSystemId = useCurrentSystem().data?.id ?? null;
  const { byId: staticById } = useStaticSystemsById();
  const { forCode: destinationFor } = useWhDestinationByCode();
  const [collapsed, setCollapsed] = useState(false);

  const [dialog, setDialog] = useState<{
    systemId: string;
    incoming: Signature[];
  } | null>(null);

  const targetSystemId = useMemo<string | null>(() => {
    return highlightedSystemId ?? currentSystemId;
  }, [highlightedSystemId, currentSystemId]);

  const targetSystem = useMemo(
    () => systems.find((s) => s.id === targetSystemId) ?? null,
    [systems, targetSystemId],
  );

  const [sort, setSort] = useState<SortState>(loadSort);

  useEffect(() => {
    saveSort(sort);
  }, [sort]);

  const [enabledGroups, setEnabledGroups] =
    useState<Set<SignatureGroup>>(loadFilter);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRootRef = useRef<HTMLDivElement | null>(null);

  const [contextMenu, setContextMenu] = useState<{
    scanId: string;
    x: number;
    y: number;
  } | null>(null);

  const { save: saveSignatures } = useSignatureSaver();
  const [clearArmed, setClearArmed] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);
  const clearTimerRef = useRef<number | null>(null);
  const undoSnapshot = useChainStore((s) =>
    targetSystemId ? (s.signaturesUndo[targetSystemId] ?? null) : null,
  );
  const clearSignaturesUndo = useChainStore((s) => s.clearSignaturesUndo);
  const [undoBusy, setUndoBusy] = useState(false);

  useEffect(() => {
    return () => {
      if (clearTimerRef.current != null) {
        window.clearTimeout(clearTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    saveFilter(enabledGroups);
  }, [enabledGroups]);

  const toggleGroup = useCallback((group: SignatureGroup) => {
    setEnabledGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  }, []);

  const filterActive = enabledGroups.size < ALL_GROUPS.length;

  const handleSort = useCallback((field: SortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "asc" },
    );
  }, []);

  const visibleSignatures = useMemo(() => {
    const arr = (targetSystem?.signatures ?? []).filter((sig) =>
      enabledGroups.has(sig.group),
    );
    const dir = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => compareSignatures(a, b, sort.field) * dir);
    return arr;
  }, [targetSystem, sort, enabledGroups]);

  useEffect(() => {
    setClearArmed(false);
    setContextMenu(null);
    if (clearTimerRef.current != null) {
      window.clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
  }, [targetSystem?.id]);

  useEffect(() => {
    if (!collapsed) return;
    setClearArmed(false);
    setContextMenu(null);
    if (clearTimerRef.current != null) {
      window.clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
  }, [collapsed]);

  const onUndo = useCallback(async () => {
    if (undoBusy || !targetSystem || undoSnapshot == null) return;
    const systemId = targetSystem.id;
    setUndoBusy(true);
    try {
      await saveSignatures(systemId, undoSnapshot, true);
      clearSignaturesUndo(systemId);
    } catch (err) {
      console.error("Failed to undo signatures paste", err);
    } finally {
      setUndoBusy(false);
    }
  }, [clearSignaturesUndo, saveSignatures, targetSystem, undoBusy, undoSnapshot]);

  const onClearAll = useCallback(async () => {
    if (clearBusy || !targetSystem) return;
    const current = targetSystem.signatures ?? [];
    if (current.length === 0) return;
    if (!clearArmed) {
      setClearArmed(true);
      if (clearTimerRef.current != null) {
        window.clearTimeout(clearTimerRef.current);
      }
      clearTimerRef.current = window.setTimeout(() => {
        setClearArmed(false);
        clearTimerRef.current = null;
      }, 3000);
      return;
    }
    if (clearTimerRef.current != null) {
      window.clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
    setClearBusy(true);
    try {
      await saveSignatures(targetSystem.id, []);
      setClearArmed(false);
    } catch (err) {
      setClearArmed(false);
      console.error("Failed to clear signatures", err);
    } finally {
      setClearBusy(false);
    }
  }, [clearArmed, clearBusy, saveSignatures, targetSystem]);

  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      if (chainId == null) return;
      if (isEditableTarget(event.target)) return;
      const text = event.clipboardData?.getData("text") ?? "";
      const parsed = parseScannerClipboard(text);
      if (parsed == null) return;
      event.preventDefault();
      const sysId = highlightedSystemId ?? currentSystemId;
      if (sysId == null) return;
      setCollapsed(false);
      setDialog({ systemId: sysId, incoming: parsed });
    },
    [chainId, highlightedSystemId, currentSystemId],
  );

  useEffect(() => {
    if (chainId == null) return;
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [chainId, handlePaste]);

  useEffect(() => {
    if (chainId == null) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== "s" && event.key !== "S") return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      setCollapsed((c) => !c);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [chainId]);

  useEffect(() => {
    if (chainId == null) return;
    if (collapsed) return;
    if (dialog) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!filterOpen) return;
      const target = e.target as Node;
      if (
        filterRootRef.current &&
        !filterRootRef.current.contains(target)
      ) {
        setFilterOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isEditableTarget(e.target)) return;
      if (filterOpen) {
        setFilterOpen(false);
        return;
      }
      setCollapsed(true);
    };
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [chainId, collapsed, dialog, filterOpen]);

  if (chainId == null) return null;

  const dialogSystem =
    dialog && systems.find((s) => s.id === dialog.systemId);
  const dialogSystemName = dialog
    ? (staticById(dialog.systemId)?.name ?? null)
    : null;
  const targetStatic = targetSystem
    ? (staticById(targetSystem.id) ?? null)
    : null;
  const targetSystemName = targetStatic?.name ?? null;

  if (collapsed) {
    return (
      <>
        <button
          type="button"
          className="signatures-panel__tab"
          onClick={() => setCollapsed(false)}
          aria-label="Show signatures panel"
        >
          <ChevronUp size={14} aria-hidden />
          Signatures
        </button>
        {dialog && (
          <SignaturesImportDialog
            open
            onOpenChange={(open) => !open && setDialog(null)}
            systemId={dialog.systemId}
            systemName={dialogSystemName}
            current={dialogSystem?.signatures ?? []}
            incoming={dialog.incoming}
          />
        )}
      </>
    );
  }

  const signatures = targetSystem?.signatures ?? [];

  const sortIndicator = (field: SortField) =>
    sort.field === field ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

  const headerAria = (field: SortField): "ascending" | "descending" | "none" =>
    sort.field === field
      ? sort.dir === "asc"
        ? "ascending"
        : "descending"
      : "none";

  return (
    <>
      <aside
        className="signatures-panel"
        role="complementary"
        aria-label="Signatures"
      >
        <header className="signatures-panel__header">
          <h2 className="signatures-panel__title">
            {targetSystem && targetStatic ? (
              <>
                <ClassBadge systemClass={targetStatic.class} />
                <span>
                  {targetSystemName ?? targetSystem.id} - Signatures (
                  {signatures.length})
                </span>
              </>
            ) : (
              "Signatures"
            )}
          </h2>
          <div className="signatures-panel__actions">
            <div className="signatures-panel__filter" ref={filterRootRef}>
              <button
                type="button"
                className="signatures-panel__icon-btn"
                onClick={() => setFilterOpen((v) => !v)}
                aria-expanded={filterOpen}
                aria-haspopup="dialog"
                aria-label="Filter signatures by group"
                title="Filter by group"
              >
                <Filter size={14} aria-hidden />
                {filterActive && (
                  <span
                    className="signatures-panel__filter-dot"
                    aria-hidden
                  />
                )}
              </button>
              {filterOpen && (
                <div
                  className="signatures-panel__filter-popover"
                  role="dialog"
                  aria-label="Filter signatures"
                >
                  {ALL_GROUPS.map((group) => (
                    <label
                      key={group}
                      className="signatures-panel__filter-row"
                    >
                      <input
                        type="checkbox"
                        checked={enabledGroups.has(group)}
                        onChange={() => toggleGroup(group)}
                      />
                      <SignatureGroupIcon group={group} />
                      <span>{GROUP_LABELS[group]}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className="signatures-panel__icon-btn signatures-panel__icon-btn--undo"
              onClick={onUndo}
              disabled={undoBusy || !targetSystem || undoSnapshot == null}
              aria-label="Undo last paste"
              title="Undo last paste"
            >
              <RotateCcw size={14} aria-hidden />
            </button>
            <button
              type="button"
              className={`signatures-panel__icon-btn ${clearArmed ? "signatures-panel__icon-btn--armed" : "signatures-panel__icon-btn--clear"}`}
              onClick={onClearAll}
              disabled={clearBusy || !targetSystem || signatures.length === 0}
              aria-label={
                clearArmed
                  ? "Click again to clear all signatures"
                  : "Clear all signatures"
              }
              title={
                clearArmed
                  ? "Click again to clear all signatures"
                  : "Clear all signatures"
              }
            >
              {clearArmed ? (
                <AlertTriangle size={14} aria-hidden />
              ) : (
                <Trash2 size={14} aria-hidden />
              )}
            </button>
            <button
              type="button"
              className="signatures-panel__icon-btn"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse signatures panel"
            >
              <ChevronDown size={16} aria-hidden />
            </button>
          </div>
        </header>
        <div className="signatures-panel__body">
          {!targetSystem && (
            <p className="signatures-panel__empty">
              Select a system, or jump in-game, to see its signatures.
            </p>
          )}
          {targetSystem && signatures.length === 0 && (
            <p className="signatures-panel__empty">
              No signatures. Paste scanner output to import.
            </p>
          )}
          {targetSystem &&
            signatures.length > 0 &&
            visibleSignatures.length === 0 && (
              <p className="signatures-panel__empty">
                All signatures are hidden by the current filter.
              </p>
            )}
          {targetSystem && visibleSignatures.length > 0 && (
            <table className="signatures-panel__table">
              <thead>
                <tr>
                  <th aria-hidden className="signatures-panel__icon" />
                  <th scope="col" aria-sort={headerAria("scan_id")}>
                    <button
                      type="button"
                      className="signatures-panel__sort"
                      onClick={() => handleSort("scan_id")}
                    >
                      ID{sortIndicator("scan_id")}
                    </button>
                  </th>
                  <th scope="col" aria-sort={headerAria("name")}>
                    <button
                      type="button"
                      className="signatures-panel__sort"
                      onClick={() => handleSort("name")}
                    >
                      Name{sortIndicator("name")}
                    </button>
                  </th>
                  <th
                    scope="col"
                    aria-sort={headerAria("group")}
                    className="signatures-panel__group"
                  >
                    <button
                      type="button"
                      className="signatures-panel__sort"
                      onClick={() => handleSort("group")}
                    >
                      Group{sortIndicator("group")}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleSignatures.map((sig) => (
                  <tr
                    key={sig.scan_id}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      setContextMenu({
                        scanId: sig.scan_id,
                        x: e.clientX,
                        y: e.clientY,
                      });
                    }}
                  >
                    <td className="signatures-panel__icon">
                      <SignatureGroupIcon group={sig.group} />
                    </td>
                    <td>
                      <code>{sig.scan_id}</code>
                    </td>
                    <td className="signatures-panel__name">
                      {sig.name || "—"}
                    </td>
                    <td className="signatures-panel__group">
                      <SignatureGroupCell
                        sig={sig}
                        destinationFor={destinationFor}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </aside>
      {contextMenu && targetSystem && (
        <SignatureContextMenu
          systemId={targetSystem.id}
          signatures={targetSystem.signatures ?? []}
          scanId={contextMenu.scanId}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
        />
      )}
      {dialog && (
        <SignaturesImportDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          systemId={dialog.systemId}
          systemName={dialogSystemName}
          current={dialogSystem?.signatures ?? []}
          incoming={dialog.incoming}
        />
      )}
    </>
  );
}
